import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { loadMunicipalities } from "@/components/OntarioMapView";
import { MapIcon, X } from "lucide-react";

/** Phase 9.2 — WHAT MATTERS ACROSS ONTARIO? Public community issue map.
 * Privacy: only municipalities/wards that independently meet the reporting threshold
 * are shaded. Everything else is neutral: "Not enough responses to display".
 * Individual responses are never mapped. */

const SOUTH_ONTARIO_BOUNDS = [[42.0, -84.0], [46.5, -74.3]];
const SHADES = ["#c7ddf5", "#8fbbe8", "#5590d2", "#2f6bb0", "#1e3a5f"];

const shadeFor = (percent) => {
  if (percent >= 60) return SHADES[4];
  if (percent >= 45) return SHADES[3];
  if (percent >= 30) return SHADES[2];
  if (percent >= 15) return SHADES[1];
  return SHADES[0];
};

const RANGES = [
  { id: "30d", label: "30 Days" }, { id: "90d", label: "90 Days" },
  { id: "election", label: "Election Period" }, { id: "all", label: "All Time" },
];

export default function OntarioIssueMap() {
  const [munis, setMunis] = useState(null);
  const [data, setData] = useState(null);
  const [issue, setIssue] = useState("top");
  const [range, setRange] = useState("all");
  const [selected, setSelected] = useState(null); // clicked shaded area
  const [wardView, setWardView] = useState(null); // {slug, geojson, stats}
  const [wardErr, setWardErr] = useState(null);

  useEffect(() => {
    loadMunicipalities().then(setMunis).catch(() => setMunis({ type: "FeatureCollection", features: [] }));
  }, []);

  const load = useCallback(() => {
    setData(null);
    const params = { range };
    if (issue !== "top") params.issue = issue;
    api.get("/public/intelligence/issue-map", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [issue, range]);
  useEffect(() => { load(); setSelected(null); setWardView(null); }, [load]);

  const areaBySlug = useMemo(() => {
    const m = {};
    (data?.areas || []).forEach((a) => { if (a.slug) m[a.slug] = a; });
    return m;
  }, [data]);

  const muniStyle = (feature) => {
    const a = areaBySlug[feature.properties.slug];
    if (!a) {
      return { color: "#94a3b8", weight: 0.6, fillColor: "#e2e8f0", fillOpacity: 0.35 };
    }
    const intensity = issue === "top" ? a.top_issue_percent : (a.issue_percent ?? 0);
    return { color: "#1e3a5f", weight: 1.4, fillColor: shadeFor(intensity), fillOpacity: 0.65 };
  };

  const onEachMuni = (feature, layer) => {
    const a = areaBySlug[feature.properties.slug];
    if (!a) {
      layer.bindTooltip(`${feature.properties.name} — Not enough responses to display`, { sticky: true });
      return;
    }
    const line = issue === "top"
      ? `Top concern: ${a.top_issue} (${a.top_issue_percent}%)`
      : `${issue}: ${a.issue_percent}%`;
    layer.bindTooltip(`${feature.properties.name} — ${line} · Responses: ${a.response_count}`, { sticky: true });
    layer.on({ click: () => { setSelected(a); setWardView(null); setWardErr(null); } });
  };

  const openWardView = async () => {
    if (!selected?.slug) return;
    setWardErr(null);
    try {
      const [stats, geo] = await Promise.all([
        api.get(`/public/intelligence/issue-map/wards/${selected.slug}`,
                { params: issue !== "top" ? { issue, range } : { range } }),
        api.get(`/public/geo/wards/${selected.slug}`),
      ]);
      setWardView({ slug: selected.slug, stats: stats.data, geojson: geo.data });
    } catch (e) {
      setWardErr(e.response?.data?.detail || "Ward view unavailable.");
    }
  };

  const wardStatsByLabel = useMemo(() => {
    const m = {};
    (wardView?.stats?.wards || []).forEach((w) => { m[w.ward] = w; });
    return m;
  }, [wardView]);

  const wardBounds = useMemo(() => {
    if (!wardView?.geojson) return null;
    try { const b = L.geoJSON(wardView.geojson).getBounds(); return b.isValid() ? b : null; } catch { return null; }
  }, [wardView]);

  return (
    <div className="wyv-container py-10 sm:py-14">
      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Community Voice</Badge>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="issue-map-title">
        What matters across Ontario?
      </h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Municipalities are shaded using real Community Poll responses. Areas without enough
        responses stay neutral — individual responses are never mapped.
      </p>

      <div className="flex flex-wrap items-end gap-4 mt-6">
        <div className="w-full sm:w-64">
          <Label className="text-xs text-muted-foreground">View</Label>
          <Select value={issue} onValueChange={setIssue}>
            <SelectTrigger className="mt-1 bg-white" data-testid="issue-map-issue-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="top">Top Concern</SelectItem>
              {(data?.issues || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Tabs value={range} onValueChange={setRange}>
          <TabsList className="h-auto flex-wrap justify-start" data-testid="issue-map-range-tabs">
            {RANGES.map((r) => <TabsTrigger key={r.id} value={r.id} className="text-xs sm:text-sm">{r.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6 items-start">
        <div className="lg:col-span-2 min-w-0">
          <div className="relative z-0 rounded-xl overflow-hidden border border-slate-200 h-[420px] sm:h-[560px]" data-testid="issue-map-map">
            {munis && data ? (
              <MapContainer bounds={SOUTH_ONTARIO_BOUNDS} scrollWheelZoom className="h-full w-full" attributionControl>
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                           url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <GeoJSON key={`${issue}-${range}-${(data.areas || []).length}`} data={munis}
                         style={muniStyle} onEachFeature={onEachMuni} />
                {wardView?.geojson && (
                  <GeoJSON key={`wards-${wardView.slug}-${issue}`} data={wardView.geojson}
                           style={(f) => {
                             const w = wardStatsByLabel[f.properties.ward_label];
                             if (!w?.available) return { color: "#94a3b8", weight: 1, fillColor: "#e2e8f0", fillOpacity: 0.5, dashArray: "4 3" };
                             const intensity = issue === "top" ? w.top_issue_percent : (w.issue_percent ?? 0);
                             return { color: "#0f766e", weight: 1.6, fillColor: shadeFor(intensity), fillOpacity: 0.7 };
                           }}
                           onEachFeature={(f, layer) => {
                             const w = wardStatsByLabel[f.properties.ward_label];
                             if (!w?.available) {
                               layer.bindTooltip(`${f.properties.ward_label} — Not enough responses to display`, { sticky: true });
                             } else {
                               const line = issue === "top" ? `Top: ${w.top_issue} (${w.top_issue_percent}%)` : `${issue}: ${w.issue_percent}%`;
                               layer.bindTooltip(`${f.properties.ward_label} — ${line} · Responses: ${w.response_count}`, { sticky: true });
                             }
                           }} />
                )}
              </MapContainer>
            ) : <Skeleton className="h-full w-full" />}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-muted-foreground" data-testid="issue-map-legend">
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm inline-block" style={{ background: "#e2e8f0", border: "1px solid #94a3b8" }} /> Not enough responses to display</span>
            {SHADES.map((c, i) => (
              <span key={c} className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm inline-block" style={{ background: c }} /> {["<15%", "15%+", "30%+", "45%+", "60%+"][i]}</span>
            ))}
          </div>
        </div>

        <div className="space-y-4 min-w-0">
          {selected ? (
            <Card data-testid="issue-map-popup">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-heading font-bold text-lg text-foreground">{selected.name}</h2>
                  <button onClick={() => { setSelected(null); setWardView(null); }} aria-label="Close" data-testid="issue-map-popup-close"><X className="h-4 w-4 text-muted-foreground" /></button>
                </div>
                <p className="text-sm text-foreground mt-2">Top Community Concern: <span className="font-semibold">{selected.top_issue}</span></p>
                <p className="text-xs font-semibold text-foreground mt-1" data-testid="issue-map-popup-responses">Responses: {selected.response_count}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-3 mb-1.5">Other leading concerns</p>
                <ul className="space-y-1">
                  {selected.leading.slice(1).map((l) => (
                    <li key={l.label} className="text-xs flex justify-between"><span>{l.label}</span><span className="tabular-nums text-muted-foreground">{l.percent}%</span></li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button asChild size="sm" className="bg-accent hover:bg-accent/90" data-testid="issue-map-view-voice">
                    <Link to={`/community-voice?municipality=${selected.municipality_id}`}>View Community Voice →</Link>
                  </Button>
                  <Button size="sm" variant="outline" onClick={openWardView} data-testid="issue-map-ward-view">
                    <MapIcon className="h-4 w-4 mr-1.5" /> View by Ward
                  </Button>
                </div>
                {wardErr && <p className="text-[11px] text-amber-700 mt-2" data-testid="issue-map-ward-error">{wardErr}</p>}
                {wardView && (
                  <p className="text-[11px] text-muted-foreground mt-2" data-testid="issue-map-ward-note">
                    Ward view uses verified boundaries. Wards without enough responses stay neutral.
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="p-5">
              <p className="text-sm text-muted-foreground" data-testid="issue-map-hint">
                Click a shaded municipality to see its top community concerns.
                {data && data.areas.length === 0 && " No areas currently meet the reporting threshold — results appear as residents answer Community Polls."}
              </p>
            </CardContent></Card>
          )}
          <Card><CardContent className="p-5">
            <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="issue-map-disclaimer">
              {data?.note} {data?.disclaimer}
            </p>
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}
